/**
 * Asset Slice
 * Manages asset state and operations
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../services/api';

const initialState = {
  loading: false,
  assetList: [],
  projectAssetList: [],
  assetHistoryList: [],
  selectedAssets: [],
  assetTypes: [],
  assetSubTypes: [],
  assetOwners: [],
  lastProject: 'none',
  error: null,
};

// Async thunks
export const fetchAllAssets = createAsyncThunk(
  'asset/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const result = await api.listAssets({ limit: 2000 });
      return result.items;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAssetsByCustomer = createAsyncThunk(
  'asset/fetchByCustomer',
  async ({ customerId, projectId = 'none' }, { rejectWithValue }) => {
    try {
      const params = projectId !== 'none' ? { projectId } : {};
      const result = await api.getAssetsByCustomer(customerId, params);
      return { items: result.items, projectId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAssetsByProject = createAsyncThunk(
  'asset/fetchByProject',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const result = await api.getAssetsByProject(projectId);
      return { items: result.items, projectId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAsset = createAsyncThunk(
  'asset/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const result = await api.getAsset(id);
      return result.item;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAssetHistory = createAsyncThunk(
  'asset/fetchHistory',
  async ({ assetId, startTs, endTs }, { rejectWithValue }) => {
    try {
      const result = await api.getAssetHistory(assetId, { startTs, endTs });
      return result.items;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAssetTypes = createAsyncThunk(
  'asset/fetchTypes',
  async (_, { rejectWithValue }) => {
    try {
      const result = await api.listAssetTypes();
      return result.items;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAssetSubTypes = createAsyncThunk(
  'asset/fetchSubTypes',
  async (_, { rejectWithValue }) => {
    try {
      const result = await api.listAssetSubtypes();
      return result.items;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAssetOwners = createAsyncThunk(
  'asset/fetchOwners',
  async (_, { rejectWithValue }) => {
    try {
      const result = await api.listAssetOwners();
      return result.items;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAssetAction = createAsyncThunk(
  'asset/create',
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.createAsset(data);
      return result.item;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAssetAction = createAsyncThunk(
  'asset/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await api.updateAsset(id, data);
      return result.item;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAssetAction = createAsyncThunk(
  'asset/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.deleteAsset(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const assetSlice = createSlice({
  name: 'asset',
  initialState,
  reducers: {
    initAssets: () => initialState,
    
    setSelectedAssets: (state, action) => {
      state.selectedAssets = action.payload;
    },
    
    addAssetTypes: (state, action) => {
      const newTypes = action.payload;
      const existingIds = new Set(state.assetTypes.map(t => t.id));
      const uniqueNewTypes = newTypes.filter(t => !existingIds.has(t.id));
      state.assetTypes = [...state.assetTypes, ...uniqueNewTypes];
    },
    
    addAssetOwners: (state, action) => {
      const newOwners = action.payload;
      const existingIds = new Set(state.assetOwners.map(o => o.id));
      const uniqueNewOwners = newOwners.filter(o => !existingIds.has(o.id));
      state.assetOwners = [...state.assetOwners, ...uniqueNewOwners];
    },
    
    addAssets: (state, action) => {
      const { assetList, nextToken } = action.payload;
      const payloadAssetIds = new Set(assetList.map(a => a.id));
      
      state.loading = !!nextToken;
      state.assetList = [
        ...assetList,
        ...state.assetList.filter(asset => !payloadAssetIds.has(asset.id)),
      ];
    },
    
    addProjectAssets: (state, action) => {
      const { projectId, projectAssetList, nextToken } = action.payload;
      const payloadAssetIds = new Set(projectAssetList.map(a => a.id));
      
      const newList = projectId !== state.lastProject
        ? [...projectAssetList]
        : [
            ...projectAssetList,
            ...state.projectAssetList.filter(a => !payloadAssetIds.has(a.id)),
          ];
      
      state.loading = !!nextToken;
      state.projectAssetList = newList;
      state.lastProject = projectId;
    },
    
    clearProjectAssets: (state) => {
      state.projectAssetList = [];
      state.lastProject = 'none';
    },
    
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all assets
      .addCase(fetchAllAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.assetList = action.payload;
      })
      .addCase(fetchAllAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch by customer
      .addCase(fetchAssetsByCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssetsByCustomer.fulfilled, (state, action) => {
        state.loading = false;
        const { items, projectId } = action.payload;
        
        if (projectId === 'none') {
          state.assetList = items;
        } else {
          state.projectAssetList = items;
          state.lastProject = projectId;
        }
      })
      .addCase(fetchAssetsByCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch by project
      .addCase(fetchAssetsByProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAssetsByProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projectAssetList = action.payload.items;
        state.lastProject = action.payload.projectId;
      })
      .addCase(fetchAssetsByProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch history
      .addCase(fetchAssetHistory.fulfilled, (state, action) => {
        state.assetHistoryList = action.payload;
      })
      
      // Fetch types
      .addCase(fetchAssetTypes.fulfilled, (state, action) => {
        state.assetTypes = action.payload;
      })
      
      // Fetch subtypes
      .addCase(fetchAssetSubTypes.fulfilled, (state, action) => {
        state.assetSubTypes = action.payload;
      })
      
      // Fetch owners
      .addCase(fetchAssetOwners.fulfilled, (state, action) => {
        state.assetOwners = action.payload;
      })
      
      // Create
      .addCase(createAssetAction.fulfilled, (state, action) => {
        const asset = action.payload;
        if (asset.projectId === 'none') {
          state.assetList = [...state.assetList, asset];
        } else {
          state.projectAssetList = [...state.projectAssetList, asset];
        }
      })
      
      // Update
      .addCase(updateAssetAction.fulfilled, (state, action) => {
        const asset = action.payload;
        if (asset.projectId === 'none') {
          state.assetList = state.assetList.map(a => 
            a.id === asset.id ? asset : a
          );
        } else {
          state.projectAssetList = state.projectAssetList.map(a => 
            a.id === asset.id ? asset : a
          );
        }
      })
      
      // Delete
      .addCase(deleteAssetAction.fulfilled, (state, action) => {
        const id = action.payload;
        state.assetList = state.assetList.filter(a => a.id !== id);
        state.projectAssetList = state.projectAssetList.filter(a => a.id !== id);
      });
  },
});

export const {
  initAssets,
  setSelectedAssets,
  addAssetTypes,
  addAssetOwners,
  addAssets,
  addProjectAssets,
  clearProjectAssets,
  setLoading,
} = assetSlice.actions;

export default assetSlice.reducer;
